/*!
 * Size chat — "Unsure about your size? Chat with me"
 * Rule-based size assistant for Shopify product pages. No external calls, no tracking.
 *
 * How it decides
 *   Measurements → scored against SIZE_CHART. Bust decides first (as the chart notes for
 *                  T-shirts and hoodies), waist and hip can pull one size, shoulder nudges.
 *   Usual size   → parsed from free text (XXS–3XL, EU 32–46, UK 4–18, US 0–14) and mapped 1:1.
 *   Both answer as "Letter/EU" (e.g. "L/40"), limited to the sizes this product actually has.
 *
 * Edit SIZE_CHART, WEIGHTS or STRINGS to tune; everything else adapts.
 * Public API (handy for testing in the console): window.SizeChat.recommend({bust,waist,hip,shoulder}),
 * window.SizeChat.parseUsualSize("M at Zara"), window.SizeChat.compose(...)
 */
(function (global) {
  'use strict';
  if (global.SizeChat) return;

  /* ------------------------------------------------------------------ data */

  // Women's body measurement chart in cm. Source: GTecom size sheet (Sept 2026).
  var SIZE_CHART = [
    { alpha: 'XXS', eu: 32, uk: 4,  bust: [75, 80],   waist: [56, 61],   hip: [81, 86],   shoulder: 36.5 },
    { alpha: 'XS',  eu: 34, uk: 6,  bust: [81, 87],   waist: [62, 67],   hip: [87, 92],   shoulder: 37.25 },
    { alpha: 'S',   eu: 36, uk: 8,  bust: [88, 93],   waist: [68, 73],   hip: [93, 99],   shoulder: 38 },
    { alpha: 'M',   eu: 38, uk: 10, bust: [94, 101],  waist: [74, 83],   hip: [100, 109], shoulder: 38.75 },
    { alpha: 'L',   eu: 40, uk: 12, bust: [102, 109], waist: [84, 94],   hip: [110, 119], shoulder: 39.5 },
    { alpha: 'XL',  eu: 42, uk: 14, bust: [110, 119], waist: [95, 105],  hip: [120, 129], shoulder: 40.3 },
    { alpha: 'XXL', eu: 44, uk: 16, bust: [120, 129], waist: [106, 117], hip: [130, 139], shoulder: 42.5 },
    { alpha: '3XL', eu: 46, uk: 18, bust: [130, 139], waist: [118, 129], hip: [140, 149], shoulder: 43 }
  ];

  // Penalty per cm outside a size's range. Bust dominates on purpose.
  var WEIGHTS = { bust: 3, waist: 1.5, hip: 1, shoulder: 0.5 };

  // Sanity limits for the form, in cm.
  var LIMITS = { bust: [60, 180], waist: [45, 170], hip: [65, 190], shoulder: [30, 60] };

  var STRINGS = {
    en: {
      title: 'Size help',
      close: 'Close',
      intro: 'Hi! I can help you find the right size for this item. How would you like to start?',
      chipMeasure: 'Send my measurements',
      chipUsual: 'Tell you my usual size',
      askMeasure: 'Enter your body measurements in cm. Measure snug over light clothing, not over a hoodie.',
      bust: 'Bust', waist: 'Waist', hip: 'Hip', shoulder: 'Shoulder', optional: 'optional', cm: 'cm',
      submitMeasure: 'Get my size',
      askUsual: 'Which size do you usually order, and where? For example \u201cM at Zara\u201d or \u201c38 at H&M\u201d.',
      usualPlaceholder: 'e.g. M at Zara',
      send: 'Send',
      summary: 'Bust {bust} cm, waist {waist} cm, hip {hip} cm',
      summaryShoulder: ', shoulder {shoulder} cm',
      resultMeasure: 'Based on your measurements I\u2019d go with',
      resultUsual: 'Then this should be your size here as well:',
      notAvailSmaller: '{wanted} isn\u2019t available for this item \u2014 it starts at {size}, which I\u2019d take. Expect a slightly roomier fit.',
      notAvailLarger: '{wanted} isn\u2019t available for this item \u2014 the largest is {size}, which I\u2019d take. It may sit a little closer.',
      notAvailNearest: '{wanted} isn\u2019t available for this item, so I\u2019d take the closest size, {size}.',
      noteLooser: 'Your waist or hips sit in {alt}. This item is sized mainly by bust, so {size} should fit \u2014 pick {alt} if you like a relaxed fit.',
      noteUp: 'You\u2019re close to {alt} too \u2014 pick {alt} if you prefer a looser fit.',
      noteDown: 'You\u2019re close to {alt} too \u2014 pick {alt} if you prefer a closer fit.',
      edgeLow: 'Your bust is below the smallest size on our chart, so this is the closest match.',
      edgeHigh: 'Your bust is above the largest size on our chart, so this is the closest match.',
      readAs: 'I read that as {what}.',
      noParse: 'I couldn\u2019t spot a size in that. Try just the size, like \u201cM\u201d or \u201c38\u201d.',
      selectSize: 'Select {size}',
      selected: '{size} is selected \u2014 you\u2019re all set.',
      startOver: 'Start over',
      errRange: 'Enter a value between {min} and {max} cm.',
      errRequired: 'Bust, waist and hip are needed for a recommendation.'
    },
    de: {
      title: 'Gr\u00f6\u00dfenberatung',
      close: 'Schlie\u00dfen',
      intro: 'Hallo! Ich helfe dir, die richtige Gr\u00f6\u00dfe f\u00fcr diesen Artikel zu finden. Womit m\u00f6chtest du starten?',
      chipMeasure: 'Meine Ma\u00dfe senden',
      chipUsual: 'Meine \u00fcbliche Gr\u00f6\u00dfe nennen',
      askMeasure: 'Gib deine K\u00f6rperma\u00dfe in cm ein. Miss eng anliegend \u00fcber leichter Kleidung, nicht \u00fcber einem Hoodie.',
      bust: 'Brustumfang', waist: 'Taille', hip: 'H\u00fcfte', shoulder: 'Schulterbreite', optional: 'optional', cm: 'cm',
      submitMeasure: 'Gr\u00f6\u00dfe ermitteln',
      askUsual: 'Welche Gr\u00f6\u00dfe bestellst du normalerweise, und wo? Zum Beispiel \u201eM bei Zara\u201c oder \u201e38 bei H&M\u201c.',
      usualPlaceholder: 'z. B. M bei Zara',
      send: 'Senden',
      summary: 'Brust {bust} cm, Taille {waist} cm, H\u00fcfte {hip} cm',
      summaryShoulder: ', Schulter {shoulder} cm',
      resultMeasure: 'Nach deinen Ma\u00dfen w\u00fcrde ich diese Gr\u00f6\u00dfe nehmen:',
      resultUsual: 'Dann sollte das auch hier deine Gr\u00f6\u00dfe sein:',
      notAvailSmaller: '{wanted} gibt es f\u00fcr diesen Artikel nicht \u2013 er beginnt bei {size}, und die w\u00fcrde ich nehmen. Die Passform f\u00e4llt dann etwas weiter aus.',
      notAvailLarger: '{wanted} gibt es f\u00fcr diesen Artikel nicht \u2013 die gr\u00f6\u00dfte Gr\u00f6\u00dfe ist {size}, und die w\u00fcrde ich nehmen. Sie kann etwas enger sitzen.',
      notAvailNearest: '{wanted} gibt es f\u00fcr diesen Artikel nicht, deshalb w\u00fcrde ich die n\u00e4chstgelegene Gr\u00f6\u00dfe {size} nehmen.',
      noteLooser: 'Taille oder H\u00fcfte liegen bei {alt}. Dieser Artikel wird vor allem nach Brustumfang bemessen, daher sollte {size} passen \u2013 nimm {alt}, wenn du es lieber locker magst.',
      noteUp: '{alt} liegt auch nah dran \u2013 nimm {alt}, wenn du es lieber weiter magst.',
      noteDown: '{alt} liegt auch nah dran \u2013 nimm {alt}, wenn du es lieber enger magst.',
      edgeLow: 'Dein Brustumfang liegt unter der kleinsten Gr\u00f6\u00dfe unserer Tabelle, das ist die n\u00e4chstgelegene Gr\u00f6\u00dfe.',
      edgeHigh: 'Dein Brustumfang liegt \u00fcber der gr\u00f6\u00dften Gr\u00f6\u00dfe unserer Tabelle, das ist die n\u00e4chstgelegene Gr\u00f6\u00dfe.',
      readAs: 'Ich habe das als {what} verstanden.',
      noParse: 'Ich konnte keine Gr\u00f6\u00dfe erkennen. Schreib einfach nur die Gr\u00f6\u00dfe, z. B. \u201eM\u201c oder \u201e38\u201c.',
      selectSize: '{size} ausw\u00e4hlen',
      selected: '{size} ist ausgew\u00e4hlt \u2013 alles bereit.',
      startOver: 'Neu starten',
      errRange: 'Bitte gib einen Wert zwischen {min} und {max} cm ein.',
      errRequired: 'F\u00fcr eine Empfehlung brauche ich Brust, Taille und H\u00fcfte.'
    }
  };

  // Spellings that shops and shoppers use, mapped to the chart's alpha names.
  var CANON = {
    '2XS': 'XXS', 'XXS': 'XXS',
    'XS': 'XS', 'XSMALL': 'XS', 'X-SMALL': 'XS',
    'S': 'S', 'SMALL': 'S',
    'M': 'M', 'MEDIUM': 'M',
    'L': 'L', 'LARGE': 'L',
    'XL': 'XL', 'XLARGE': 'XL', 'X-LARGE': 'XL',
    'XXL': 'XXL', '2XL': 'XXL', 'XXLARGE': 'XXL', 'XX-LARGE': 'XXL',
    '3XL': '3XL', 'XXXL': '3XL', 'XXX-LARGE': '3XL',
    '4XL': '4XL', 'XXXXL': '4XL', '5XL': '5XL'
  };

  /* ---------------------------------------------------------------- engine */

  function fill(s, vars) {
    return String(s).replace(/\{(\w+)\}/g, function (_, k) { return vars[k] == null ? '' : vars[k]; });
  }
  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  function dist(v, range) {
    if (v < range[0]) return range[0] - v;
    if (v > range[1]) return v - range[1];
    return 0;
  }

  // Row whose range holds v. In a gap or beyond the ends: the nearest row, larger on ties.
  function indexFor(v, key) {
    var best = 0, bestD = Infinity;
    for (var i = 0; i < SIZE_CHART.length; i++) {
      var d = dist(v, SIZE_CHART[i][key]);
      if (d <= bestD) { bestD = d; best = i; }
    }
    return best;
  }

  function recommend(m) {
    var scores = SIZE_CHART.map(function (row) {
      var s = WEIGHTS.bust * dist(m.bust, row.bust)
            + WEIGHTS.waist * dist(m.waist, row.waist)
            + WEIGHTS.hip * dist(m.hip, row.hip);
      if (m.shoulder) s += WEIGHTS.shoulder * Math.abs(m.shoulder - row.shoulder);
      return s;
    });
    var best = 0;
    for (var i = 1; i < scores.length; i++) if (scores[i] <= scores[best]) best = i; // ties → larger
    var alt = null;
    if (best + 1 < scores.length && scores[best + 1] - scores[best] <= 2) alt = best + 1;
    else if (best - 1 >= 0 && scores[best - 1] - scores[best] <= 2) alt = best - 1;
    var first = SIZE_CHART[0], last = SIZE_CHART[SIZE_CHART.length - 1];
    return {
      method: 'measurements',
      index: best,
      alt: alt,
      bustIndex: indexFor(m.bust, 'bust'),
      waistIndex: indexFor(m.waist, 'waist'),
      hipIndex: indexFor(m.hip, 'hip'),
      edge: m.bust < first.bust[0] ? 'low' : (m.bust > last.bust[1] ? 'high' : null),
      scores: scores
    };
  }

  function canon(s) {
    var k = String(s == null ? '' : s).toUpperCase().replace(/\s+/g, '');
    if (CANON[k]) return CANON[k];
    var head = k.split('/')[0];              // "M/38" → "M"
    return CANON[head] || head;
  }
  function chartIndex(alpha) {
    for (var i = 0; i < SIZE_CHART.length; i++) if (SIZE_CHART[i].alpha === alpha) return i;
    return -1;
  }
  function findBy(key, val) {
    for (var i = 0; i < SIZE_CHART.length; i++) if (SIZE_CHART[i][key] === val) return i;
    return -1;
  }

  // Token boundaries: "H&M" must not read as size M, "US 8" must not read as size S.
  var B = '(?:^|[\\s,;:/(\u201e\u201c"\'])';
  var E = '(?=$|[\\s,;:/.)!?\u201c\u201d"\'])';
  var ALPHA_RE = new RegExp(B + '(XXXS|2XS|XXS|XS|X-SMALL|XSMALL|S|SMALL|M|MEDIUM|L|LARGE|XL|X-LARGE|XLARGE|XXL|2XL|XX-LARGE|XXLARGE|XXXL|3XL|XXX-LARGE|4XL|XXXXL|5XL)' + E);
  var EU_RE   = new RegExp(B + '(?:EU\\s*)?(3[2468]|4[0246])' + E);
  var UK_RE   = new RegExp(B + 'UK\\s*(4|6|8|10|12|14|16|18)' + E);
  var US_RE   = new RegExp(B + 'US\\s*(0|2|4|6|8|10|12|14)' + E);
  var BARE_RE = new RegExp(B + '(4|6|8|10|12|14|16|18)' + E);

  function parseUsualSize(text) {
    var t = String(text || '').toUpperCase(), m;
    if ((m = t.match(ALPHA_RE))) {
      var alpha = canon(m[1]), idx = chartIndex(alpha);
      if (idx > -1) return { method: 'usual', index: idx, raw: m[1] };
      return { method: 'usual', index: SIZE_CHART.length, raw: alpha };       // 4XL, 5XL: beyond the chart
    }
    if ((m = t.match(EU_RE)))   return { method: 'usual', index: findBy('eu', +m[1]), raw: m[1] };
    if ((m = t.match(UK_RE)))   return { method: 'usual', index: findBy('uk', +m[1]), raw: 'UK ' + m[1], what: 'UK ' + m[1] };
    if ((m = t.match(US_RE))) {
      var uk = +m[1] + 4;
      return { method: 'usual', index: findBy('uk', uk), raw: 'US ' + m[1], what: 'US ' + m[1] + ' \u2248 UK ' + uk };
    }
    if ((m = t.match(BARE_RE))) return { method: 'usual', index: findBy('uk', +m[1]), raw: m[1], what: 'UK ' + m[1] };
    return null;
  }

  // Which chart rows this product carries, as the shop's own labels (e.g. "2XL"). Parallel to SIZE_CHART.
  function availability(sizes) {
    var labels = [], any = false, i;
    for (i = 0; i < SIZE_CHART.length; i++) labels.push(null);
    (sizes || []).forEach(function (s) {
      var k = canon(s), idx = chartIndex(k);
      if (idx < 0 && /^\d+$/.test(k)) idx = findBy('eu', +k);
      if (idx > -1) { labels[idx] = String(s).trim(); any = true; }
    });
    if (!any) for (i = 0; i < SIZE_CHART.length; i++) labels[i] = SIZE_CHART[i].alpha;
    return labels;
  }

  // "L/40" — shop label first, chart alpha as fallback, EU number from the chart.
  function format(index, labels) {
    var row = SIZE_CHART[index];
    if (!row) return '';
    var label = labels && labels[index];
    if (!label || /^\d+$/.test(label)) label = row.alpha;
    return label + '/' + row.eu;
  }

  // Snap a chart index onto a size the product actually has.
  function resolve(index, labels) {
    var avail = [], i;
    for (i = 0; i < labels.length; i++) if (labels[i]) avail.push(i);
    var lo = avail[0], hi = avail[avail.length - 1];
    if (index < lo) return { index: lo, note: 'smaller' };
    if (index > hi) return { index: hi, note: 'larger' };
    if (labels[index]) return { index: index, note: null };
    var best = lo;
    for (i = 0; i < avail.length; i++) if (Math.abs(avail[i] - index) <= Math.abs(best - index)) best = avail[i];
    return { index: best, note: 'nearest' };
  }

  // Turn a recommendation (from recommend() or parseUsualSize()) into the words the bot says.
  function compose(t, labels, src) {
    var r = resolve(src.index, labels);
    var size = format(r.index, labels);
    var wanted = (src.index >= 0 && src.index < SIZE_CHART.length) ? format(src.index, labels) : src.raw;
    var out = { size: size, label: labels[r.index] || SIZE_CHART[r.index].alpha, pre: null, lead: '', notes: [] };
    if (src.what) out.pre = fill(t.readAs, { what: src.what });
    if (r.note) { out.lead = fill(t['notAvail' + cap(r.note)], { wanted: wanted, size: size }); return out; }
    if (src.method === 'usual') { out.lead = t.resultUsual; return out; }
    out.lead = t.resultMeasure;
    var up = src.index + 1, down = src.index - 1;
    if (src.edge) out.notes.push(src.edge === 'low' ? t.edgeLow : t.edgeHigh);
    else if ((src.waistIndex > src.index || src.hipIndex > src.index) && labels[up]) out.notes.push(fill(t.noteLooser, { alt: format(up, labels), size: size }));
    else if (src.alt === up && labels[up]) out.notes.push(fill(t.noteUp, { alt: format(up, labels) }));
    else if (src.alt === down && down >= 0 && labels[down]) out.notes.push(fill(t.noteDown, { alt: format(down, labels) }));
    return out;
  }

  global.SizeChat = {
    chart: SIZE_CHART, weights: WEIGHTS, limits: LIMITS, strings: STRINGS,
    recommend: recommend, parseUsualSize: parseUsualSize, availability: availability,
    format: format, resolve: resolve, compose: compose
  };

  if (typeof document === 'undefined') return;

  /* ------------------------------------------------------------------- UI */

  var ICON_CLOSE = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3 3l10 10M13 3L3 13" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function reduceMotion() {
    return !!(global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function norm(s) { return String(s == null ? '' : s).trim().toUpperCase().replace(/\s+/g, ''); }

  // Find the theme's own control for a size, so "Select M" can click it. Best effort, theme-agnostic.
  function findVariantControl(label) {
    var want = norm(label), i, n;
    if (!want) return null;
    var radios = document.querySelectorAll('input[type="radio"]');
    for (i = 0; i < radios.length; i++) if (norm(radios[i].value) === want) return { type: 'radio', node: radios[i] };
    var selects = document.querySelectorAll('select');
    for (i = 0; i < selects.length; i++) {
      for (n = 0; n < selects[i].options.length; n++) {
        var o = selects[i].options[n];
        if (norm(o.value) === want || norm(o.text) === want) return { type: 'select', node: selects[i], value: o.value };
      }
    }
    var scopes = 'form[action*="/cart/add"] button, [data-product-form] button, .product-form button, variant-selects button, variant-radios button, product-info button, .product button';
    var buttons = document.querySelectorAll(scopes);
    for (i = 0; i < buttons.length; i++) {
      if (norm(buttons[i].textContent) === want && !buttons[i].closest('.szc-panel')) return { type: 'button', node: buttons[i] };
    }
    return null;
  }
  function reveal(node) {
    if (node && node.scrollIntoView) node.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
  }

  // Analytics hook. Every event goes out as:
  //   - a DOM event            document → "size-chat:open" (detail = payload + extra)
  //   - a Shopify pixel event  Shopify.analytics.publish("size_chat_open", payload)  → Settings › Customer events
  //   - GA4 / GTM              gtag("event", "size_chat_open", payload) or dataLayer.push({event: ...})
  // Payload stays flat and free of personal data (no measurements), which is what GA4 accepts.
  function track(name, payload, extra) {
    var snake = 'size_chat_' + name, k;
    var detail = {};
    for (k in payload) detail[k] = payload[k];
    for (k in extra || {}) detail[k] = extra[k];
    try { document.dispatchEvent(new CustomEvent('size-chat:' + name, { detail: detail })); } catch (e) {}
    try {
      if (global.Shopify && global.Shopify.analytics && typeof global.Shopify.analytics.publish === 'function') {
        global.Shopify.analytics.publish(snake, payload);
      }
    } catch (e) {}
    try {
      if (typeof global.gtag === 'function') {
        global.gtag('event', snake, payload);
      } else if (global.dataLayer && typeof global.dataLayer.push === 'function') {
        var row = { event: snake };
        for (k in payload) row[k] = payload[k];
        global.dataLayer.push(row);
      }
    } catch (e) {}
  }
  function selectVariant(label) {
    var c = findVariantControl(label);
    if (!c) return false;
    if (c.type === 'radio') {
      if (!c.node.checked) c.node.click();
      reveal(c.node.id ? (document.querySelector('label[for="' + c.node.id + '"]') || c.node) : c.node);
    } else if (c.type === 'select') {
      c.node.value = c.value;
      c.node.dispatchEvent(new Event('change', { bubbles: true }));
      reveal(c.node);
    } else {
      c.node.click();
      reveal(c.node);
    }
    return true;
  }

  function readConfig(root) {
    var node = root && root.querySelector('[data-szc-config]');
    try { return node ? (JSON.parse(node.textContent) || {}) : {}; } catch (e) { return {}; }
  }

  var uid = 0;

  function Widget(cfg) {
    var self = this;
    var t = STRINGS[String(cfg.lang || 'de').toLowerCase().slice(0, 2)] || STRINGS.de;
    var labels = availability(cfg.sizes);
    var id = 'szc-panel-' + (++uid);

    var panel = el('div', 'szc-panel');
    panel.id = id;
    panel.tabIndex = -1;
    panel.hidden = true;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', t.title);
    panel.innerHTML =
      '<div class="szc-head"><span class="szc-title"></span><button type="button" class="szc-close"></button></div>' +
      '<div class="szc-thread" aria-live="polite"></div>' +
      '<div class="szc-foot" hidden></div>';
    panel.querySelector('.szc-title').textContent = t.title;
    var closeBtn = panel.querySelector('.szc-close');
    closeBtn.setAttribute('aria-label', t.close);
    closeBtn.innerHTML = ICON_CLOSE;
    var thread = panel.querySelector('.szc-thread');
    var foot = panel.querySelector('.szc-foot');
    var backdrop = el('div', 'szc-backdrop');
    backdrop.hidden = true;
    document.body.appendChild(backdrop);
    document.body.appendChild(panel);

    var trigger = null, lastFocus = null, started = false, focusInside = false, counted = false;

    document.addEventListener('focusin', function (e) { focusInside = panel.contains(e.target); });
    document.addEventListener('pointerdown', function (e) { if (!panel.contains(e.target)) focusInside = false; });
    closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', close);
    panel.addEventListener('keydown', function (e) { if (e.key === 'Escape') { e.stopPropagation(); close(); } });

    function scrollThread() { thread.scrollTop = thread.scrollHeight; }
    function focusFirst() {
      var f = foot.querySelector('button, input');
      if (f && focusInside) f.focus({ preventScroll: true });
    }
    function setFoot(node) {
      foot.innerHTML = '';
      if (node) foot.appendChild(node);
      foot.hidden = !node;
      scrollThread();
    }
    function addUser(text) {
      thread.appendChild(el('div', 'szc-msg szc-msg--user', text));
      scrollThread();
    }
    // build: a string, or a function that fills the message element.
    function addBot(build, cb) {
      var typing = el('div', 'szc-msg szc-msg--bot szc-typing');
      typing.setAttribute('aria-hidden', 'true');
      typing.innerHTML = '<i></i><i></i><i></i>';
      thread.appendChild(typing);
      scrollThread();
      var len = typeof build === 'string' ? build.length : 80;
      var delay = reduceMotion() ? 0 : 450 + Math.min(650, len * 5);
      setTimeout(function () {
        var msg = el('div', 'szc-msg szc-msg--bot');
        if (typeof build === 'string') msg.appendChild(el('p', null, build)); else build(msg);
        if (typing.parentNode === thread) thread.replaceChild(msg, typing); else thread.appendChild(msg);
        scrollThread();
        if (cb) cb(msg);
      }, delay);
    }
    function chips(items) {
      var wrap = el('div', 'szc-chips');
      items.forEach(function (it) {
        var b = el('button', 'szc-chip', it.label);
        b.type = 'button';
        b.addEventListener('click', it.onClick);
        wrap.appendChild(b);
      });
      return wrap;
    }

    function start() {
      thread.innerHTML = '';
      setFoot(null);
      addBot(t.intro, function () {
        setFoot(chips([
          { label: t.chipMeasure, onClick: function () {
            addUser(t.chipMeasure); setFoot(null);
            addBot(t.askMeasure, function () { setFoot(measureForm()); focusFirst(); });
          } },
          { label: t.chipUsual, onClick: function () {
            addUser(t.chipUsual); setFoot(null);
            addBot(t.askUsual, function () { setFoot(textForm()); focusFirst(); });
          } }
        ]));
        focusFirst();
      });
    }

    function measureForm() {
      var form = el('form', 'szc-form');
      form.noValidate = true;
      var grid = el('div', 'szc-grid');
      var keys = ['bust', 'waist', 'hip', 'shoulder'];
      keys.forEach(function (key) {
        var field = el('label', 'szc-field');
        var name = el('span', null, t[key]);
        if (key === 'shoulder') {
          name.appendChild(document.createTextNode(' '));
          name.appendChild(el('small', null, '(' + t.optional + ')'));
        }
        var box = el('div', 'szc-input');
        var input = document.createElement('input');
        input.type = 'text';                 // text + decimal keypad: accepts "96,5" and "96.5" everywhere
        input.inputMode = 'decimal';
        input.name = key;
        input.autocomplete = 'off';
        input.setAttribute('aria-required', key === 'shoulder' ? 'false' : 'true');
        box.appendChild(input);
        box.appendChild(el('em', null, t.cm));
        field.appendChild(name);
        field.appendChild(box);
        grid.appendChild(field);
      });
      var err = el('p', 'szc-error');
      err.hidden = true;
      err.setAttribute('role', 'alert');
      var btn = el('button', 'szc-btn', t.submitMeasure);
      btn.type = 'submit';
      form.appendChild(grid);
      form.appendChild(err);
      form.appendChild(btn);

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var m = {}, bad = null;
        keys.forEach(function (key) {
          var input = form.elements[key];
          var raw = String(input.value).trim().replace(',', '.');
          var v = parseFloat(raw);
          input.parentNode.classList.remove('is-invalid');
          if (key === 'shoulder' && raw === '') { m.shoulder = null; return; }
          if (raw === '' || isNaN(v)) {
            if (!bad) bad = { key: key, msg: t.errRequired };
            input.parentNode.classList.add('is-invalid');
            return;
          }
          if (v < LIMITS[key][0] || v > LIMITS[key][1]) {
            if (!bad) bad = { key: key, msg: fill(t.errRange, { min: LIMITS[key][0], max: LIMITS[key][1] }) };
            input.parentNode.classList.add('is-invalid');
            return;
          }
          m[key] = v;
        });
        if (bad) { err.textContent = bad.msg; err.hidden = false; form.elements[bad.key].focus(); return; }
        err.hidden = true;
        addUser(fill(t.summary, m) + (m.shoulder ? fill(t.summaryShoulder, m) : ''));
        setFoot(null);
        showResult(compose(t, labels, recommend(m)), 'measurements', m);
      });
      return form;
    }

    function textForm() {
      var form = el('form', 'szc-form szc-form--text');
      form.noValidate = true;
      var row = el('div', 'szc-row');
      var input = document.createElement('input');
      input.type = 'text';
      input.name = 'usual';
      input.placeholder = t.usualPlaceholder;
      input.autocomplete = 'off';
      input.maxLength = 120;
      input.setAttribute('aria-label', t.askUsual);
      var btn = el('button', 'szc-btn', t.send);
      btn.type = 'submit';
      row.appendChild(input);
      row.appendChild(btn);
      form.appendChild(row);
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var text = input.value.trim();
        if (!text) { input.focus(); return; }
        addUser(text);
        input.value = '';
        var parsed = parseUsualSize(text);
        if (!parsed) { addBot(t.noParse, function () { input.focus(); }); return; }
        setFoot(null);
        showResult(compose(t, labels, parsed), 'usual-size', { text: text, interpreted: parsed.raw });
      });
      return form;
    }

    function showResult(res, method, input) {
      addBot(function (msg) {
        msg.classList.add('szc-msg--result');
        if (res.pre) msg.appendChild(el('p', null, res.pre));
        msg.appendChild(el('p', null, res.lead));
        msg.appendChild(el('div', 'szc-size', res.size));
        res.notes.forEach(function (n) { msg.appendChild(el('p', 'szc-note', n)); });
      }, function () {
        var actions = el('div', 'szc-actions');
        if (findVariantControl(res.label)) {
          var sel = el('button', 'szc-btn', fill(t.selectSize, { size: res.size }));
          sel.type = 'button';
          sel.addEventListener('click', function () {
            if (!selectVariant(res.label)) return;
            sel.disabled = true;
            track('select', { product: String(cfg.key == null ? '' : cfg.key), method: method, size: res.size });
            addBot(fill(t.selected, { size: res.size }), function () { setTimeout(close, 1200); });
          });
          actions.appendChild(sel);
        }
        var again = el('button', 'szc-link', t.startOver);
        again.type = 'button';
        again.addEventListener('click', start);
        actions.appendChild(again);
        setFoot(actions);
        focusFirst();
      });
      track('recommendation', { product: String(cfg.key == null ? '' : cfg.key), method: method, size: res.size }, { label: res.label, input: input });
    }

    function open() {
      lastFocus = document.activeElement;
      panel.hidden = false;
      backdrop.hidden = false;
      panel.classList.add('is-open');
      document.documentElement.classList.add('szc-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      panel.focus({ preventScroll: true });
      focusInside = true;
      if (!started) { started = true; start(); }
      if (!counted) { counted = true; track('open', { product: String(cfg.key == null ? '' : cfg.key) }); }
      scrollThread();
    }
    function close() {
      if (panel.hidden) return;
      panel.hidden = true;
      backdrop.hidden = true;
      panel.classList.remove('is-open');
      document.documentElement.classList.remove('szc-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      var back = (trigger && document.contains(trigger)) ? trigger : lastFocus;
      if (back && back.focus) back.focus({ preventScroll: true });
    }

    // Hook a trigger button up (called again if the theme re-renders the product form).
    self.attach = function (btn) {
      if (!btn) return;
      trigger = btn;
      btn.setAttribute('aria-haspopup', 'dialog');
      btn.setAttribute('aria-controls', id);
      btn.setAttribute('aria-expanded', panel.hidden ? 'false' : 'true');
    };
    self.toggle = function () { if (panel.hidden) open(); else close(); };
    self.open = open;
    self.close = close;
  }

  // One widget per product, shared by every trigger that points at it (main form, sticky bar, re-renders).
  var widgets = {};
  function widgetFor(root) {
    var cfg = readConfig(root);
    var key = cfg.key != null ? String(cfg.key) : 'default';
    if (!widgets[key]) widgets[key] = new Widget(cfg);
    widgets[key].attach(root.querySelector('[data-szc-open]'));
    return widgets[key];
  }
  function init() {
    var roots = document.querySelectorAll('[data-szc]');
    for (var i = 0; i < roots.length; i++) widgetFor(roots[i]);
  }

  // Delegated so triggers keep working after a theme swaps the product form's HTML.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-szc-open]') : null;
    if (!btn) return;
    var root = btn.closest('[data-szc]');
    if (!root) return;
    e.preventDefault();
    widgetFor(root).toggle();
  });

  global.SizeChat.init = init;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})(typeof window !== 'undefined' ? window : globalThis);